import { apiFetch } from '@/lib/api';

// Operator pricing-admin + compute/costs client (VERTICAL-PRICING-ADMIN-1).
// Backed by the operator-gated /api/v1/admin/verticals/{vertical}/* endpoints.

export interface AdminTier {
  tier_key: string;
  display_name: string;
  monthly_price_usd: number;
  credit_grant_mode?: "compatibility" | "price_default" | "fixed_total";
  included_credits: number | null;
  included_credits_usd: number;
  monthly_credits: number;
  paddle_price_monthly: string | null;
  is_free: boolean;
  subscribe_ready: boolean;
  sort_order: number;
  is_visible: boolean;
  is_default: boolean;
}

export interface VerticalPricingResponse {
  vertical_id: string;
  credit_exchange_rate: number;
  environment: string;
  tiers: AdminTier[];
}

export interface TierUpdatePayload {
  monthly_price_usd?: number | string;
  credit_grant_mode?: "price_default" | "fixed_total";
  // Sent as the typed text (an exact decimal string) so the browser never rounds it.
  included_credits?: number | string;
  product_id?: string;
  confirm?: boolean;
}

export interface TierUpdateResponse {
  vertical_id: string;
  tier: AdminTier;
  repointed_paddle_price_id: string | null;
  invariant_ok: boolean;
}

export interface PegUpdateResponse {
  vertical_id: string;
  credit_exchange_rate: number;
  previous_credit_exchange_rate: number;
}

export interface CostSinkRow {
  sink: string;
  usd: number;
  count: number;
}

export interface CostRevenueRow {
  type: string;
  usd: number;
  count: number;
}

export interface CostTenantRow {
  tenant_id: string;
  granted_usd: number;
  spent_usd: number;
  balance_usd: number;
}

export interface CostDailyRow {
  date: string;
  revenue_usd: number;
  compute_usd: number;
}

export interface VerticalCostsResponse {
  vertical_id: string;
  days: number;
  period_start: string;
  period_end: string;
  revenue_in_usd: number;
  promo_in_usd: number;
  granted_in_usd: number;
  compute_out_usd: number;
  expiration_usd: number;
  total_spend_usd: number;
  net_usd: number;
  outstanding_balance_usd: number;
  by_sink: CostSinkRow[];
  by_revenue_type: CostRevenueRow[];
  by_tenant: CostTenantRow[];
  daily: CostDailyRow[];
}

const v = (vertical: string) => encodeURIComponent(vertical);
const k = (tierKey: string) => encodeURIComponent(tierKey);

export const verticalPricingApi = {
  getPricing: (vertical: string): Promise<VerticalPricingResponse> =>
    apiFetch(`/api/v1/admin/verticals/${v(vertical)}/pricing`),

  updateTier: (
    vertical: string,
    tierKey: string,
    payload: TierUpdatePayload,
  ): Promise<TierUpdateResponse> =>
    apiFetch(`/api/v1/admin/verticals/${v(vertical)}/pricing/${k(tierKey)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  updatePeg: (
    vertical: string,
    payload: { credit_exchange_rate: number; confirm?: boolean },
  ): Promise<PegUpdateResponse> =>
    apiFetch(`/api/v1/admin/verticals/${v(vertical)}/peg`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  getCosts: (vertical: string, days = 30): Promise<VerticalCostsResponse> =>
    apiFetch(`/api/v1/admin/verticals/${v(vertical)}/costs?days=${days}`),
};
